import { MetroImage } from 'components/city-blocks/metro-image';
import Link from 'next/link';
import React, { useMemo, useState } from 'react';
import { RegionInfoFragment } from 'utils/gqlReactTypings.generated.d';
import { encodeRegionPageHref } from 'utils/urls';

interface IProps {
  title: string;
  regions: RegionInfoFragment[];
  className?: string;
}

export const MetrosListSection: React.FC<IProps> = ({ title, regions, className }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredRegions = useMemo(() => {
    if (!searchQuery.trim()) {
      return regions;
    }

    const query = searchQuery.toLowerCase();
    return regions.filter(
      (region) => region.displayName.toLowerCase().includes(query) || region.name.toLowerCase().includes(query)
    );
  }, [regions, searchQuery]);

  return (
    <div className={`mb-8 flex flex-col gap-6 text-eggplant md:mb-16 md:gap-8 ${className || ''}`}>
      <div className='flex w-full flex-row items-start justify-between gap-4'>
        <div className='flex flex-col items-start justify-start'>
          <div className='text-red-950 leading-trim-both text-edge-cap justify-start text-xl font-bold leading-tight sm:text-2xl md:text-3xl'>
            {title}
          </div>
        </div>
        <div className='flex w-full flex-col items-end justify-end md:w-64'>
          <div className='flex w-full flex-row items-center gap-3 rounded-lg border border-neutral-200 bg-white px-3 py-2.5 text-base text-neutral-800'>
            <i className='fa-solid fa-magnifying-glass text-eggplant-500'></i>
            <input
              placeholder='Search metros...'
              type='text'
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.currentTarget.value)}
              className='w-full border-none bg-white text-eggplant-500 placeholder:font-medium placeholder:text-neutral-700 focus:border-none'
            />
          </div>
        </div>
      </div>

      <div className='grid w-full grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 lg:grid-cols-3'>
        {filteredRegions.map((region) => (
          <Link key={region.id} href={encodeRegionPageHref(region)}>
            <div className='inline-flex w-full items-center justify-between rounded-lg bg-white p-4 shadow-md outline outline-1 outline-offset-0 outline-neutral-100 transition-shadow hover:shadow-lg'>
              <div className='flex items-center justify-center gap-3'>
                <div className='size-10 shrink-0'>
                  <MetroImage region={region} className='size-10' />
                </div>
                <div className='justify-center text-sm font-medium leading-relaxed text-eggplant sm:text-base md:text-lg'>
                  {region.displayName}
                </div>
              </div>
              <i className='fa fa-chevron-right text-red-950 text-sm' />
            </div>
          </Link>
        ))}
      </div>

      {filteredRegions.length === 0 && searchQuery && (
        <div className='py-8 text-center text-neutral-500'>No metros found matching "{searchQuery}"</div>
      )}
    </div>
  );
};
